import { WEEKDAYS } from '../catalog/format'

// Calendar dates are "YYYY-MM-DD" strings on the business's wall clock. All
// arithmetic happens at UTC midnight, so no device time zone or clock change
// can shift a day.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function toDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`)
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10)
}

// todayIso is the device's local date.
export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function addDays(iso: string, days: number): string {
  const date = toDate(iso)
  date.setUTCDate(date.getUTCDate() + days)
  return toIso(date)
}

// addMonths keeps the day of the month, or stops at the last day of a shorter
// month: 31 January and one month is 28 February.
export function addMonths(iso: string, months: number): string {
  const date = toDate(iso)
  const firstOfTarget = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1))
  const lastDay = new Date(Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0)).getUTCDate()
  firstOfTarget.setUTCDate(Math.min(date.getUTCDate(), lastDay))
  return toIso(firstOfTarget)
}

// weekdayIndex returns 0 for Monday, as working hours store it.
export function weekdayIndex(iso: string): number {
  return (toDate(iso).getUTCDay() + 6) % 7
}

export function weekStart(iso: string): string {
  return addDays(iso, -weekdayIndex(iso))
}

// monthGridRange is the Monday-to-Sunday weeks covering iso's month: at most
// six weeks, which is 42 days, the most the API returns in one request.
export function monthGridRange(iso: string): { from: string; to: string } {
  const first = `${iso.slice(0, 7)}-01`
  const last = addDays(addMonths(first, 1), -1)
  return { from: weekStart(first), to: addDays(weekStart(last), 6) }
}

// formatMonth shows "November 2026".
export function formatMonth(iso: string): string {
  const date = toDate(iso)
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

// formatLongDay shows "Tuesday 15 September".
export function formatLongDay(iso: string): string {
  const date = toDate(iso)
  return `${WEEKDAYS[weekdayIndex(iso)]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`
}

// formatWeekRange shows the Monday-to-Sunday week starting on from:
// "14–20 September", or "28 September – 4 October" across two months.
export function formatWeekRange(from: string): string {
  const start = toDate(from)
  const end = toDate(addDays(from, 6))
  const month = (d: Date) => MONTHS[d.getUTCMonth()]
  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()}–${end.getUTCDate()} ${month(end)}`
  }
  return `${start.getUTCDate()} ${month(start)} – ${end.getUTCDate()} ${month(end)}`
}
